import { call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects'
import { SchemaVideoShareInfoResult, fetchVideoShare } from 'api'
import { type PreloadShare, selectPreloadShare } from 'redux/selector/preloadConfig'
import { preloadConfigActions } from 'redux/slice/preloadConfig'
import { shareVideoActions } from 'redux/slice/shareVideo'
import { pickRandom } from 'utils/pickRandom'
import { buildShareUrl, isUsableShareResponse } from 'utils/shareLink'

/**
 * 等待 preload config 抵達終局狀態的上限。
 * 必須大於 getMetadataJSONWithUrl 的 TIMEOUT_MS(5000)，否則成功的請求會被這層 timeout 搶先，
 * 造成合法分享被誤判成失敗。
 */
const PRELOAD_WAIT_MS = 8000

/** preload 載入的終局 action（成功或失敗），等待方以此判斷可以停止等待 */
const PRELOAD_TERMINAL_ACTIONS = [
  preloadConfigActions.setPreloadConfig.type,
  preloadConfigActions.fetchPreloadConfigFailure.type,
]

function* fetchShareVideoSaga(action: ReturnType<typeof shareVideoActions.fetchShareVideo>): Generator {
  try {
    let preload = (yield select(selectPreloadShare)) as PreloadShare

    // preload 尚未抵達終局狀態時先等待，避免把「還沒載入完」誤判成「沒有可用網域」
    if (!preload.loaded) {
      yield race({
        done: take(PRELOAD_TERMINAL_ACTIONS),
        timeout: delay(PRELOAD_WAIT_MS),
      })
      preload = (yield select(selectPreloadShare)) as PreloadShare
    }

    // 已達終局但清單為空：啟動時可能網路失敗、或讀到過期快取。
    // 既有的 precachePreloadConfig（app.ts:271）可能在那之後才補寫了有效快取，
    // 但它不會更新 Redux，因此這裡主動重抓一次，避免整個 session 卡在降級狀態。
    // 僅在確實抵達終局狀態時才重試；若上一步是 timeout（loaded 仍為 false，請求還在飛），
    // 直接往下走降級，不再疊加第二個 8 秒等待
    if (preload.loaded && (preload.oriShare.length === 0 || preload.webShare.length === 0)) {
      yield put(preloadConfigActions.fetchPreloadConfig())
      yield race({
        done: take(PRELOAD_TERMINAL_ACTIONS),
        timeout: delay(PRELOAD_WAIT_MS),
      })
      preload = (yield select(selectPreloadShare)) as PreloadShare
    }

    // 測速選出的網域優先；includes 這道檢查同時處理「背景刷新換掉清單」的情況——
    // 舊結果不在當前清單時視同沒測過，退回隨機，不會送出已下線的網域。
    const { oriShareSpeedTest } = preload
    const speedTestedDomain =
      oriShareSpeedTest.status === 'done' &&
      oriShareSpeedTest.fastest !== null &&
      preload.oriShare.includes(oriShareSpeedTest.fastest)
        ? oriShareSpeedTest.fastest
        : undefined

    // 測速未完成或全數未通過驗證時沿用隨機挑選：不等測速，也不退回清單第一個（會讓流量集中）。
    // pickRandom 回傳 T | undefined，strict 模式下必須顯式收斂，不得用非空斷言
    const oriShareDomain = speedTestedDomain ?? pickRandom(preload.oriShare)
    const shareBase = pickRandom(preload.webShare)
    if (oriShareDomain === undefined || shareBase === undefined) {
      yield put(shareVideoActions.setFallbackToShare())
      return
    }

    // 背景刷新（不等待）：本次分享照用現有網域、零額外延遲，
    // 但若目前資料來自過期快取，下次分享就會用到刷新後的網域。
    // 這條路徑處理「非空但已過期」的情況——它不會觸發上面的空清單 retry。
    yield put(preloadConfigActions.refreshPreloadConfig())

    // WARN: force cast string to number because js do not support int64.
    const response = (yield call(fetchVideoShare, action.payload as unknown as number, {
      resource: oriShareDomain,
    })) as SchemaVideoShareInfoResult

    // fail-closed：缺 H265、分享字串為空、或簽章不完整時一律降級
    if (!isUsableShareResponse(response)) {
      yield put(shareVideoActions.setFallbackToShare())
      return
    }

    // VideoShare 與 cloudfront 皆為後端加密字串，前端原樣透傳，不做任何加解密
    const shareUrl = buildShareUrl(shareBase, response.VideoShare, response.cloudfront)
    if (shareUrl === null) {
      yield put(shareVideoActions.setFallbackToShare())
      return
    }

    yield put(shareVideoActions.setShareData({ shareUrl, videoShare: response.VideoShare }))
  } catch (error) {
    // 使用者只會看到降級後的推廣頁，詳細原因僅在開發環境輸出，不外洩內部細節
    if (process.env.NODE_ENV !== 'production') {
      console.warn('fetchShareVideoSaga failed, falling back to share page', error)
    }
    yield put(shareVideoActions.setFallbackToShare())
  }
}

export default function* shareVideoSaga() {
  yield takeLatest(shareVideoActions.fetchShareVideo.type, fetchShareVideoSaga)
}
